import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Shield } from 'lucide-react';
import { Alert, AlertDescription } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { PARENTAL_CONSENT_AGE_THRESHOLD } from '@jeraisonne/db';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConsentRow {
  id?: string;
  is_minor: boolean;
  parent_email: string | null;
  can_share_publicly: boolean;
  can_use_selfie: boolean;
  can_use_video: boolean;
  can_be_reposted_by_jeraisonne: boolean;
  use_anonymous_pseudo: boolean;
}

const DEFAULTS: ConsentRow = {
  is_minor: false,
  parent_email: null,
  can_share_publicly: true,
  can_use_selfie: false,
  can_use_video: false,
  can_be_reposted_by_jeraisonne: false,
  use_anonymous_pseudo: false,
};

// ─── Toggle row ──────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start justify-between gap-4 py-4 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
    >
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
          checked ? 'bg-accent-400' : 'bg-gray-200'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-[#ffffff] shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

// ─── Privacy page ─────────────────────────────────────────────────────────────

export function AccountPrivacyPage() {
  const { user, profile } = useAuth();
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState<ConsentRow | null>(null);

  const isStudent = profile?.role === 'student';

  const { data: consent, isLoading } = useQuery({
    queryKey: ['parental-consent', user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async (): Promise<ConsentRow | null> => {
      const { data, error } = await supabase
        .from('parental_consents')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (error) throw error;
      return data as ConsentRow | null;
    },
  });

  // Initialiser le formulaire une fois les données chargées
  useEffect(() => {
    if (consent !== undefined) {
      setForm(consent ?? DEFAULTS);
    }
  }, [consent]);

  // Calculer si mineur selon la birthdate du student_profile
  const { data: studentProfile } = useQuery({
    queryKey: ['student-profile-birthdate', user?.id],
    enabled: isStudent && !!user?.id,
    staleTime: 300_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('student_profiles')
        .select('birthdate')
        .eq('id', user!.id)
        .maybeSingle();
      return data;
    },
  });

  const isMinor = (() => {
    if (!studentProfile?.birthdate) return false;
    const birthdate = new Date(studentProfile.birthdate);
    const age = (Date.now() - birthdate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    return age < PARENTAL_CONSENT_AGE_THRESHOLD;
  })();

  const currentForm = form ?? DEFAULTS;

  const setField = <K extends keyof ConsentRow>(key: K, value: ConsentRow[K]) => {
    setForm((prev) => ({ ...(prev ?? DEFAULTS), [key]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async (data: ConsentRow) => {
      const payload = {
        user_id: user!.id,
        is_minor: isMinor,
        parent_email: data.parent_email,
        can_share_publicly: data.can_share_publicly,
        can_use_selfie: isMinor ? false : data.can_use_selfie,
        can_use_video: isMinor ? false : data.can_use_video,
        can_be_reposted_by_jeraisonne: isMinor ? false : data.can_be_reposted_by_jeraisonne,
        use_anonymous_pseudo: data.use_anonymous_pseudo,
        accepted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase.from('parental_consents').upsert(payload, {
        onConflict: 'user_id',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parental-consent', user?.id] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-sm text-gray-400">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link to="/profile" className="rounded-xl p-2 text-gray-500 hover:bg-gray-100">
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <div className="flex items-center gap-2">
          <Shield className="text-primary-500 h-5 w-5" />
          <h1 className="text-lg font-bold text-gray-900">Confidentialité & Partage</h1>
        </div>
      </div>

      {saved && (
        <Alert className="mb-4">
          <AlertDescription>Préférences enregistrées.</AlertDescription>
        </Alert>
      )}

      {isMinor && (
        <div className="mb-4 rounded-2xl bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-800">Compte mineur</p>
          <p className="mt-1 text-xs text-amber-700">
            Certaines options avancées (selfie, vidéo, republication) sont désactivées pour les
            moins de {PARENTAL_CONSENT_AGE_THRESHOLD} ans conformément au RGPD.
          </p>
        </div>
      )}

      {/* Partage */}
      <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Partage de défis
          </p>
        </div>

        <div className="px-4">
          <ToggleRow
            label="Autoriser le partage public"
            description="Permet de créer et partager des défis basés sur tes exercices."
            checked={currentForm.can_share_publicly}
            onChange={(v) => setField('can_share_publicly', v)}
          />
        </div>

        <div className="px-4">
          <ToggleRow
            label="Utiliser un pseudo anonyme"
            description="Ton prénom n'apparaît pas sur les pages publiques des défis."
            checked={currentForm.use_anonymous_pseudo}
            onChange={(v) => setField('use_anonymous_pseudo', v)}
          />
        </div>
      </div>

      {/* Médias */}
      <div className="mt-4 divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Médias</p>
        </div>

        <div className="px-4">
          <ToggleRow
            label="Autoriser photo selfie"
            description="Joindre une photo de toi à tes défis partagés."
            checked={currentForm.can_use_selfie}
            onChange={(v) => setField('can_use_selfie', v)}
            disabled={isMinor}
          />
        </div>

        <div className="px-4">
          <ToggleRow
            label="Autoriser vidéo réaction"
            description="Joindre une courte vidéo (max 10s) à tes défis."
            checked={currentForm.can_use_video}
            onChange={(v) => setField('can_use_video', v)}
            disabled={isMinor}
          />
        </div>

        <div className="px-4">
          <ToggleRow
            label="Autoriser republication par Jeraisonne"
            description="Jeraisonne peut partager tes défis dans ses réseaux sociaux officiels."
            checked={currentForm.can_be_reposted_by_jeraisonne}
            onChange={(v) => setField('can_be_reposted_by_jeraisonne', v)}
            disabled={isMinor}
          />
        </div>
      </div>

      {/* Email parent (si mineur) */}
      {isMinor && (
        <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-900">Email du parent / tuteur</span>
            <p className="mt-0.5 text-xs text-gray-500">
              Requis pour activer les options médias avancées.
            </p>
            <input
              type="email"
              value={currentForm.parent_email ?? ''}
              onChange={(e) => setField('parent_email', e.target.value || null)}
              placeholder="parent@exemple.fr"
              className="focus:border-primary-500 focus:ring-primary-500/20 mt-2 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2"
            />
          </label>
        </div>
      )}

      {/* Enregistrer */}
      <div className="mt-6">
        <button
          type="button"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate(currentForm)}
          className="bg-primary-500 hover:bg-primary-600 w-full rounded-2xl py-4 text-base font-semibold text-white transition-colors active:scale-[0.98] disabled:opacity-60"
        >
          {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer mes préférences'}
        </button>
        {saveMutation.isError && (
          <p className="mt-2 text-center text-sm text-red-500">
            Erreur lors de l'enregistrement. Réessaie.
          </p>
        )}
      </div>
    </div>
  );
}
